/**
 * See: https://www.gatsbyjs.com/docs/browser-apis/
 */
import { createRoot } from "react-dom/client";

import WrapWithProvider from "./wrap-with-provider";
import scrollToHash from "./src/utils/scrollToHash";

// Import global stylesheet
import "./src/styles/global.scss";

export function replaceHydrateFunction() {
    return (element, container) => {
        const root = createRoot(container);
        root.render(element);
    };
}

// Gatsby's router intercepts in-page anchors like "/#request-demo-form" and
// turns them into route changes, so the browser never performs the fragment
// navigation itself and scroll-behavior from global.scss never applies. That
// left every one of those links jumping instantly, and did nothing at all when
// the link pointed at the hash already in the address bar. Handling the
// same-page case here restores both.
const onAnchorClick = event => {
    if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
    ) {
        return;
    }

    const anchor = event.target.closest?.("a[href]");

    if (!anchor || anchor.target) {
        return;
    }

    // Some links cannot be scrolled the moment they are clicked - a link inside
    // a modal has to wait for the scroll lock to lift. Those opt out and do the
    // jump themselves.
    if (anchor.hasAttribute("data-defers-hash-scroll")) {
        return;
    }

    const url = new URL(anchor.href, window.location.href);

    // Only links into the page we are already on. Anything crossing a document
    // boundary stays with the router, which has to wait for the destination to
    // render before it can scroll anywhere.
    if (
        url.origin !== window.location.origin ||
        url.pathname !== window.location.pathname ||
        !url.hash
    ) {
        return;
    }

    if (!scrollToHash(url.hash, "smooth")) {
        return;
    }

    event.preventDefault();

    // Repeating the current hash is a no-op for the router but should still
    // leave the address bar correct on a first visit.
    if (window.location.hash !== url.hash) {
        window.history.pushState(null, "", url.hash);
    }
};

// Arriving from another page is the case that has to stay instant: the
// destination has only just rendered, and animating to a target the reader has
// not seen yet is noise. Opt out of Gatsby's own restoration, which does not
// pass an explicit option and so inherits the CSS smooth behavior.
export const shouldUpdateScroll = ({ routerProps: { location } }) => {
    if (location.hash && scrollToHash(location.hash, "instant")) {
        return false;
    }

    return true;
};

export const onClientEntry = () => {
    // Capture phase, so this runs before the router's own delegated handler.
    document.addEventListener("click", onAnchorClick, true);
};

// A cold load straight to a URL with a hash never goes through
// shouldUpdateScroll, and the browser's own fragment-scroll-on-load would
// inherit the CSS smooth behavior. Force that first scroll instant too.
export const onInitialClientRender = () => {
    if (window.location.hash) {
        scrollToHash(window.location.hash, "instant");
    }
};

export const wrapRootElement = WrapWithProvider;
